import { useDispatch } from 'react-redux'

import type { RetailOrderLineItem } from '~/api/graphql/generated/types'
import type { OptimisticOverride } from '~/components/dashboard/soapV2/charges/table/hooks/useOptimisticItemUpdates'
import {
  editChargeSheetProducerItemBatch,
  editChargeSheetRetailOrderLineItemProducerBatch,
} from '~/store/duck/clientFinanceData'
import {
  updateInvoiceLineItemProducerBatch,
  updateRetailOrderLineItemProducerBatch,
} from '~/store/reducers/invoiceV3'
import type { InvoiceLineItem } from '~/types'
import { getIsRetailOrderLineItem } from '~/utils/invoiceUtils'

/**
 * Hook for handling producer changes on charge sheet items
 * Supports both InvoiceLineItem and RetailOrderLineItem
 * Supports both charge sheet and invoice contexts
 *
 * @param item - The line item to edit
 * @param invoiceId - Optional invoice ID for invoice context
 * @param applyOptimistic - Optional callback to apply optimistic updates before API call
 * @returns Handler function that can be called directly from onChange events
 */
export const useChargeItemProducerEdit = (
  item: InvoiceLineItem | RetailOrderLineItem,
  invoiceId?: string,
  applyOptimistic?: (itemId: string, changes: OptimisticOverride) => void,
) => {
  const dispatch = useDispatch()
  const isRetailOrderLineItem = getIsRetailOrderLineItem(item)

  return (producerId: string) => {
    // Apply optimistic update immediately if callback provided
    if (applyOptimistic) {
      applyOptimistic(item.id, { producerId: producerId || '' })
    }

    // Dispatch Redux action (server update)
    if (isRetailOrderLineItem) {
      if (invoiceId) {
        // Invoice context - retail order line item
        dispatch(
          updateRetailOrderLineItemProducerBatch([
            {
              id: item.id,
              producerId: producerId || '',
              expectedModification: item.modificationDate,
            },
          ]),
        )
      } else {
        // Charge sheet context - retail order line item
        dispatch(
          editChargeSheetRetailOrderLineItemProducerBatch([
            {
              id: item.id,
              producerId: producerId || '',
              expectedModification: item.modificationDate,
            },
          ]),
        )
      }
    } else if (invoiceId) {
      // Invoice line item - Invoice context
      dispatch(
        updateInvoiceLineItemProducerBatch([
          {
            id: item.id,
            expectedModification: item.modificationDate,
            producerId,
          },
        ]),
      )
    } else {
      // Charge sheet context - invoice line item
      dispatch(
        editChargeSheetProducerItemBatch({
          clientId: item.clientId,
          updateItemProducerInput: [
            {
              producerId,
              id: item.id,
              expectedModification: item.modificationDate,
            },
          ],
        }),
      )
    }
  }
}
